<script lang="ts" setup>
import { useNewsletter } from '@/composables/useNewsletter'
import { useLang } from '~/composables/useLang'

const { t } = useLang()
const { email, hasSubscribed, handleSubmit } = useNewsletter()

const emailValidationMessages = computed(() => ({
  required: t('validation.required'),
  email: t('validation.email'),
}))
</script>

<template>
  <Container class="newsletter-component">
    <div class="flex flex-col flex-1">
      <h3 class="h4 newsletter-heading">{{ t('footer.subscribe-to-newsletter') }}</h3>
      <span> {{ t('footer.newsletter-voucher') }} </span>
    </div>
    <div class="flex flex-col flex-1">
      <FormKit
        v-slot="{ disabled }"
        v-model="email"
        name="newsletter-form"
        type="form"
        :actions="false"
        :incomplete-message="false"
        :classes="{
          form: {
            'w-full flex items-center flex-col mb-4': true,
          },
        }"
        @submit="handleSubmit">
        <div
          v-if="hasSubscribed"
          class="success flex items-center justify-center px-4 w-full"
          @click="hasSubscribed = false">
          {{ t('footer.newsletter-subscribe-success') }}
        </div>
        <FormKit
          v-else
          name="email"
          type="email"
          validation="required|email"
          :placeholder="t('quotes.email-placeholder')"
          :validation-messages="emailValidationMessages"
          :classes="{ outer: { 'flex-1 ': true } }" />
        <button class="flex items-center justify-center" :disabled="disabled">{{ t('footer.subscribe') }}</button>
      </FormKit>
      <p class="consent-block" v-html="t('footer.newsletter-consent')" />
    </div>
  </Container>
</template>

<style lang="postcss" scoped>
.newsletter-component {
  @apply flex gap-[30px] flex-wrap flex-col lg:flex-row;

  .newsletter-heading {
    @apply mb-[1.25rem] text-white;
  }

  .consent-block {
    @apply p-0 text-sm font-normal;

    :deep(a),
    a {
      @apply font-bold;
      @apply text-brand-primary focus:text-brand-primary hover:text-brand-primary;
      @apply no-underline focus:underline hover:underline;
    }
  }
}
</style>

<style lang="postcss">
.newsletter-component {
  .success {
    color: theme('colors.white');
    background: theme('colors.neutral3');
    border-radius: theme('vars.borderRadiusHeavy');
    height: 3rem;

    font-weight: bold;
    cursor: pointer;

    @screen sm {
      flex: 3;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
  }

  .formkit-outer {
    margin: 0;
    width: 100%;
    flex: 3;

    .formkit-wrapper {
      max-width: none;
    }

    .formkit-message {
      color: theme('colors.brand.primary');
    }

    .formkit-inner {
      border-radius: theme('vars.borderRadiusHeavy');
      border: 0;
      box-shadow: none;
      background-color: transparent;
      position: static;
      width: 100%;

      input {
        font-weight: bold;
        height: 3rem;
        background: theme('colors.neutral3');
        color: theme('colors.white') !important;
        border-radius: theme('vars.borderRadiusHeavy');
        border: 1px solid theme('colors.neutral3');

        padding-left: 1rem;

        &::placeholder {
          color: theme('colors.white') !important;
        }

        &:focus {
          border: 1px solid theme('colors.white');
        }

        @screen sm {
          border-top-right-radius: 0 !important;
          border-bottom-right-radius: 0 !important;
        }
      }

      @screen sm {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
      }
    }
  }

  button {
    flex: 1;
    width: 100%;
    height: 3rem;
    margin-top: 1rem;
    padding: 1rem;
    color: theme('colors.black');
    font-weight: bold;
    background: theme('colors.white');
    border-radius: 30px;
    align-self: flex-start;
    min-width: 13.25rem;
  }
}

@screen sm {
  .newsletter-component {
    button {
      margin-top: 0;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    form {
      flex-direction: row;
    }
  }
}
</style>

<style lang="postcss" scoped>
.alt .newsletter-component {
  :deep(.formkit-outer) {
    .formkit-wrapper .formkit-inner .formkit-input {
      @apply bg-white dark:bg-dmGray1 !text-brand-black border-none;

      &::placeholder {
        @apply !text-neutral4;
      }
    }

    .formkit-message {
      @apply text-white dark:text-black;
    }
  }

  .consent-block {
    :deep(a),
    a {
      @apply font-medium;
      @apply text-white focus:text-white hover:text-white dark:text-black;
      @apply underline;
    }
  }

  button {
    @apply rounded-sm lg:rounded-l-none bg-red text-white dark:text-black;
  }

  &.tp {
    @apply bg-brand-black;
  }
}

.wl .newsletter-component {
  .newsletter-heading,
  p,
  span {
    @apply text-text1;
  }
}

.tp .newsletter-component {
  button {
    @apply bg-brand-black;
  }
}

.ae .newsletter-component {
  .newsletter-heading {
    @apply text-text1;
  }

  .consent-block {
    :deep(a),
    a {
      @apply text-black focus:text-black hover:text-black;
    }
  }

  button {
    @apply bg-orange;
  }
}
</style>
